import { useEffect, useMemo, useRef, useState } from 'react'

import { SETTINGS } from '@aytm/app/stats/page/question-views/psc/lib/constants'
import { calcExportData } from '@aytm/app/stats/page/question-views/psc/lib/export-params'
import { calcReportParamsSection, resolveReportParams } from '@aytm/app/stats/page/question-views/psc/lib/report-params'

// The rendered chart height, which the pptx exporter needs to scale the exported svg.
export const calcChartHeight = (batches = []) =>
  (SETTINGS.barHeight + 2 * SETTINGS.barMargin) * batches.length + 2 * SETTINGS.chartMargin

// Owns the polarity-scale block state (mode, neutral threshold), seeded from the psc<key>
// report-param token and re-seeded whenever that token changes outside the block.
//
// The page view never parses the token itself: it receives an api on mount and pulls the
// payloads it needs (model state, report-param token, chart height, svg) when a change is
// announced through onStateChange.
export default function usePolarityScaleState({
  params,
  paramsRevision,
  format,
  batches,
  chartApiRef,
  onReady,
  onStateChange
}) {
  const needsParamsUpdate = useRef(false)

  const [state, setState] = useState(() => {
    const resolved = resolveReportParams({ section: params?.psc, format })
    needsParamsUpdate.current = resolved.needsUpdate
    return resolved.state
  })

  // every input the api payloads are built from, refreshed each render so the pull is current
  const snapshot = useRef()
  snapshot.current = { ...state, format, batches }

  const api = useMemo(
    () => ({
      getState: () => {
        const { mode, neutral } = snapshot.current
        return { mode, neutral }
      },
      getReportParamsSection: () => calcReportParamsSection(snapshot.current),
      getExportData: () => calcExportData(snapshot.current, chartApiRef.current?.getSize?.()),
      formatSVG: () => chartApiRef.current?.formatSVG?.() || ''
    }),
    [chartApiRef]
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  const pendingReason = useRef('seed')

  // The token moved outside the block (a report revert, another view of the question, the URL):
  // re-seed from it.
  //
  // Keyed on the revision the shell bumps per host render, not on the token inside the params bag.
  // A host-driven render is a fresh read of the live model; a re-render React starts for itself
  // carries the revision it already had. Keying on the token missed a restore of a value the block
  // had already moved away from locally, because the block's own write is silent -- the token the
  // block was handed had never changed, so there was nothing for a comparison to notice.
  useEffect(() => {
    const resolved = resolveReportParams({ section: params?.psc, format: snapshot.current.format })
    needsParamsUpdate.current = needsParamsUpdate.current || resolved.needsUpdate

    pendingReason.current = 'seed'
    setState(resolved.state)
  }, [params, paramsRevision])

  // The reason tells the view what the change means: 'seed' is a resolved starting state, which the
  // token already agrees with, so it is not written back; 'state' a change the researcher made.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    // a re-seed schedules a render of its own; that follow-up carries no reason and is not a change
    if (!reason && !needsParamsUpdate.current) return

    onStateChangeRef.current?.(reason || 'seed', { needsUpdate: needsParamsUpdate.current })
    needsParamsUpdate.current = false
  }, [state])

  const onModelChange = useMemo(
    () =>
      (change = {}) =>
        setState(current => {
          if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

          pendingReason.current = 'state'

          return { ...current, ...change }
        }),
    []
  )

  return { ...state, onModelChange }
}
